import ExportIcon from '@diligentcorp/atlas-react-icons/dist/esm/lens/Export.js';
import InfoIcon from '@diligentcorp/atlas-react-icons/dist/esm/lens/Info.js';
import { PageHeader } from '@diligentcorp/atlas-theme-mui-lens';
import { Box, Button, Tooltip } from '@mui/material';
import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Redirect } from 'react-router-dom';
import { EmptyState } from 'src/components/atlas/molecules';
import { DisclosuresHeroMetrics } from 'src/components/atlas/organisms/DisclosuresHeroMetrics';
import { DisclosuresTable } from 'src/components/atlas/organisms/DisclosuresTable';
import { DisclosuresToolbar } from 'src/components/atlas/organisms/DisclosuresToolbar';
import { FilterRow } from 'src/components/atlas/organisms/DisclosuresToolbar/DisclosuresFilterPopover';
import emptyStateImage from 'src/components/CategoryConfiguration/assets/atlas-empty-state.svg';
import { handleErrorToast, handleSuccessToast } from 'src/components/molecules';
import { useFlaggedCasesTable } from 'src/components/pages/CoiDisclosures/useFlaggedCasesTable';
import { useOrganisationCoiResponsesTable } from 'src/components/pages/CoiDisclosures/useOrganisationFormResponsesTable';
import { ResponseStatus } from 'src/graphql';
import {
  useCalculateTableRows,
  useIsFeatureEnabled,
  usePageQueryParams,
  useRedirect,
} from 'src/hooks';
import { AtlasMuiThemeProvider } from 'src/theme/atlas/atlasMuiThemeProvider';
import * as Types from '../../../types';
import * as styles from './CoiDisclosures.styles';
import { PageContainer } from './CoiDisclosures.styles';
import { useDisclosureMetrics } from './hooks/useDisclosureMetrics';
import { HookRow, normalizeRow } from './normalizeRow';
import {
  ACTIONS_STATUSES,
  ALL_STATUSES,
  DisclosureDataset,
  DisclosureFilterColumn,
  DisclosureFilterValue,
  MetricCardId,
  OPEN_STATUSES,
  WAITING_STATUSES,
} from './types';

const ROW_HEIGHT = 56;
const MIN_ROWS = 7;
const PAGE_SPACE = 200;

const EMPTY_FILTER_VALUE: DisclosureFilterValue = {
  status: [],
  reviewer: [],
  category: [],
  formTitle: [],
};

let filterRowSeq = 0;
const makeFilterId = () => `filter-row-${(filterRowSeq += 1)}`;

export const CoiDisclosures = () => {
  const currentUserId = useSelector(
    (state: Types.State) => state.globalUsers.currentUser?.id?.toString() ?? '',
  );
  const { showRedirect, redirectTo, redirect } = useRedirect();
  const { page, setPage } = usePageQueryParams('/disclosures');
  const { rows } = useCalculateTableRows(ROW_HEIGHT, MIN_ROWS, PAGE_SPACE);

  const [dataset, setDataset] = useState<DisclosureDataset>('open');
  const [activeCard, setActiveCard] = useState<MetricCardId | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterValue, setFilterValue] =
    useState<DisclosureFilterValue>(EMPTY_FILTER_VALUE);
  const [appliedFilterValue, setAppliedFilterValue] =
    useState<DisclosureFilterValue>(EMPTY_FILTER_VALUE);
  const [filterRows, setFilterRows] = useState<FilterRow[]>([
    { id: makeFilterId(), column: '' },
  ]);
  const [isExporting, setIsExporting] = useState(false);

  const metrics = useDisclosureMetrics(currentUserId);
  const isCustomFieldsEnabled = useIsFeatureEnabled(
    Types.FeatureFlag.CUSTOM_FIELDS,
  );

  const optionalFilters = useMemo(() => {
    const optional: {
      category?: string[];
      formTitle?: string[];
      submittedDate?: { from: string; to: string };
    } = {};
    if (appliedFilterValue.category.length) {
      optional.category = appliedFilterValue.category;
    }
    if (appliedFilterValue.formTitle.length) {
      optional.formTitle = appliedFilterValue.formTitle;
    }
    if (
      appliedFilterValue.submittedDate?.from &&
      appliedFilterValue.submittedDate?.to
    ) {
      optional.submittedDate = appliedFilterValue.submittedDate;
    }
    return optional;
  }, [appliedFilterValue]);

  const textFilterLogic = 'and' as const;

  // Custom field values hang off cases, so the auto-closed dataset (form
  // responses) cannot be filtered by them.
  const customFieldFilter = isCustomFieldsEnabled
    ? appliedFilterValue.customField
    : undefined;

  const openData = useFlaggedCasesTable(
    rows,
    page,
    {
      status: appliedFilterValue.status.length
        ? appliedFilterValue.status
        : OPEN_STATUSES,
      reviewer: appliedFilterValue.reviewer,
      ...optionalFilters,
      textFilterLogic,
      customField: customFieldFilter,
    },
    redirect,
    searchTerm,
  );
  const closedData = useFlaggedCasesTable(
    rows,
    page,
    {
      status: ['Closed'],
      reviewer: appliedFilterValue.reviewer,
      ...optionalFilters,
      textFilterLogic,
      customField: customFieldFilter,
    },
    redirect,
    searchTerm,
  );
  const autoClosedData = useOrganisationCoiResponsesTable(
    rows,
    page,
    {
      statuses: [ResponseStatus.AutoClosed],
      reviewer: appliedFilterValue.reviewer,
      ...optionalFilters,
      textFilterLogic,
    },
    searchTerm,
  );

  const cardData = useFlaggedCasesTable(
    rows,
    page,
    {
      status: appliedFilterValue.status.length
        ? appliedFilterValue.status
        : ALL_STATUSES,
      reviewer: appliedFilterValue.reviewer,
      ...optionalFilters,
      textFilterLogic,
      customField: customFieldFilter,
    },
    redirect,
    searchTerm,
  );

  const source =
    dataset === null
      ? cardData
      : dataset === 'open'
        ? openData
        : dataset === 'closed'
          ? closedData
          : autoClosedData;

  const filterOptions = useMemo(
    () =>
      dataset === 'auto-closed'
        ? {
            status: autoClosedData.statusOptions,
            category: autoClosedData.categoryOptions,
            formTitle: autoClosedData.formTitleOptions,
          }
        : {
            status: openData.statusOptions,
            reviewer: openData.reviewerOptions,
            category: openData.categoryOptions,
            formTitle: openData.formTitleOptions,
            customField: openData.customFieldOptions,
          },
    [dataset, autoClosedData, openData],
  );

  const visibleColumns: DisclosureFilterColumn[] = useMemo(() => {
    if (dataset === 'auto-closed') {
      return ['category', 'status', 'submittedDate', 'formTitle'];
    }
    const columns: DisclosureFilterColumn[] = [
      'category',
      'status',
      'reviewer',
      'submittedDate',
      'formTitle',
    ];
    if (isCustomFieldsEnabled && openData.customFieldOptions?.length) {
      columns.push('customField');
    }
    return columns;
  }, [dataset, isCustomFieldsEnabled, openData.customFieldOptions]);

  const tableRows = useMemo(
    () => ((source.tableData ?? []) as HookRow[]).map(normalizeRow),
    [source.tableData],
  );

  const setStatusFilter = (status: string[]) => {
    setFilterValue((prev) => ({ ...prev, status }));
    setAppliedFilterValue((prev) => ({ ...prev, status }));
  };
  const setReviewerFilter = (reviewer: string[]) => {
    setFilterValue((prev) => ({ ...prev, reviewer }));
    setAppliedFilterValue((prev) => ({ ...prev, reviewer }));
  };

  const handleSearch = (term: string) => {
    setSearchTerm(term);
    setPage(1);
  };

  const handleFilterChange = (value: DisclosureFilterValue) => {
    // Draft only — does not trigger queries.
    setFilterValue(value);
  };

  const handleFilterApply = (value: DisclosureFilterValue) => {
    setFilterValue(value);
    setAppliedFilterValue(value);
    setPage(1);
  };

  const resetFilters = () => {
    setFilterValue(EMPTY_FILTER_VALUE);
    setAppliedFilterValue(EMPTY_FILTER_VALUE);
    setFilterRows([{ id: makeFilterId(), column: '' }]);
    setPage(1);
  };

  const handleDatasetChange = (next: DisclosureDataset) => {
    setDataset(next);
    setActiveCard(null);
    resetFilters();
  };

  const handleCardClick = (id: MetricCardId) => {
    if (id === activeCard) {
      setActiveCard(null);
      setDataset('open');
      resetFilters();
      return;
    }
    setActiveCard(id);
    setPage(1);
    if (id === 'assigned') {
      setDataset(null);
      setStatusFilter(ALL_STATUSES);
      setReviewerFilter([currentUserId]);
    } else if (id === 'actions') {
      setDataset(null);
      setStatusFilter(ACTIONS_STATUSES);
      setReviewerFilter([currentUserId]);
    } else {
      setDataset(null);
      setStatusFilter(WAITING_STATUSES);
      setReviewerFilter([currentUserId]);
    }
  };

  const handleReset = () => {
    setSearchTerm('');
    setActiveCard(null);
    setDataset('open');
    resetFilters();
  };

  const handleExport = async () => {
    if (isExporting) return;

    setIsExporting(true);
    try {
      const response = await fetch('/api/portal/cases/coi-csv', {
        method: 'GET',
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Failed to download COI export');
      }

      const contentType = response.headers.get('content-type');
      if (contentType && !contentType.includes('text/csv')) {
        throw new Error('Invalid response format');
      }

      const blob = await response.blob();
      if (blob.size === 0) {
        throw new Error('Export contains no data');
      }

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const timestamp = new Date().toISOString().split('T')[0];
      link.download = `coi-disclosures-export-${timestamp}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      handleSuccessToast({ toastMessage: 'Export is successful' });
    } catch (error) {
      handleErrorToast({ toastMessage: 'Export failed' });
    } finally {
      setIsExporting(false);
    }
  };

  const onRowClick = (id: string) => {
    if (dataset === 'auto-closed') {
      (
        autoClosedData.openDisclosure as ((caseId: string) => void) | undefined
      )?.(id);
    } else if (dataset === 'closed') {
      closedData.onRowClick?.(id);
    } else {
      openData.onRowClick?.(id);
    }
  };

  return (
    <AtlasMuiThemeProvider>
      <PageContainer
        className="routing-body-content"
        data-testid="coi-disclosures-page"
      >
        {showRedirect && <Redirect to={redirectTo} push />}
        <PageHeader
          pageTitle="Disclosures"
          pageSubtitle="Manage and review submits to all disclosure forms"
          moreButton={
            <Box sx={styles.exportGroup}>
              <Button
                variant="contained"
                startIcon={<ExportIcon />}
                data-testid="disclosures-export"
                onClick={handleExport}
                disabled={isExporting}
              >
                {isExporting ? 'Exporting...' : 'Export'}
              </Button>
              <Tooltip title="Export is based on permissions.">
                <Box
                  component="span"
                  sx={styles.exportInfoIcon}
                  data-testid="disclosures-export-info"
                  tabIndex={0}
                >
                  <InfoIcon />
                </Box>
              </Tooltip>
            </Box>
          }
        />

        <DisclosuresHeroMetrics
          metrics={metrics}
          activeCard={activeCard}
          onCardClick={handleCardClick}
        />
        <DisclosuresToolbar
          searchTerm={searchTerm}
          onSearch={handleSearch}
          onReset={handleReset}
          dataset={dataset}
          onDatasetChange={handleDatasetChange}
          filterValue={filterValue}
          onFilterChange={handleFilterChange}
          onFilterApply={handleFilterApply}
          filterRows={filterRows}
          onFilterRowsChange={setFilterRows}
          onRemoveAllFilters={resetFilters}
          makeFilterId={makeFilterId}
          filterOptions={filterOptions}
          visibleColumns={visibleColumns}
        />
        <DisclosuresTable
          rows={tableRows}
          dataset={dataset ?? 'open'}
          isLoading={source.isLoading}
          isError={source.isError}
          page={Number(page)}
          totalPages={source.totalPages ?? 1}
          onPageChange={(p) => setPage(p)}
          onRowClick={onRowClick}
          emptyState={
            <EmptyState
              illustration={
                <img
                  src={emptyStateImage}
                  alt="No disclosures"
                  width="160"
                  height="132"
                />
              }
              title={
                source.isError
                  ? 'Error loading disclosures'
                  : 'No disclosures available'
              }
              description={
                source.isError
                  ? 'There was an issue retrieving your COI disclosures. Please try again.'
                  : 'You have currently no COI disclosures in the system.'
              }
            />
          }
        />
      </PageContainer>
    </AtlasMuiThemeProvider>
  );
};
